/**
 * 🎛️ PERSONALITY SLIDER TYPES
 * Unified type definitions for personality slider state management
 */

export interface SliderConfig {
  id: string;
  label: string;
  min: number;
  max: number;
  value: number;
  description: string;
}

export interface PersonalitySliderState {
  // Visibility and mode
  isVisible: boolean;
  mode: 'modal' | 'inline' | 'hidden';
  
  // Slider configuration and values
  configuration: SliderConfig[];
  values: Record<string, number>;
  
  // Submission state
  isSubmitting: boolean;
  hasSubmitted: boolean;
  
  // Error handling
  error: string | null;
  
  // Context for submission
  submissionContext?: {
    onboardingStep: number;
    detectedScenario: string;
    nextExpected: string;
  };
}

export interface PersonalitySliderActions {
  // Show/hide actions
  showModal: (config: SliderConfig[]) => void;
  showInline: (config: SliderConfig[]) => void;
  hide: () => void;
  
  // Value management
  updateValue: (sliderId: string, value: number) => void;
  updateValues: (values: Record<string, number>) => void;
  
  // Submission
  submit: (mode: 'modal' | 'inline', explicitValues?: Record<string, number>) => Promise<any>;
  
  // Reset
  reset: () => void;
  
  // Error handling
  setError: (error: string | null) => void;
}

export interface PersonalitySliderHookReturn {
  state: PersonalitySliderState;
  actions: PersonalitySliderActions;
}

// Default state
export const defaultPersonalitySliderState: PersonalitySliderState = {
  isVisible: false,
  mode: 'hidden',
  configuration: [],
  values: {},
  isSubmitting: false,
  hasSubmitted: false,
  error: null,
  submissionContext: undefined
};

// Default slider configuration
export const defaultSliderConfiguration: SliderConfig[] = [
  {
    id: 'energy',
    label: 'Energy & Warmth',
    min: 1,
    max: 5,
    value: 3,
    description: 'How energetic and warm should I be with you?'
  },
  {
    id: 'data_depth',
    label: 'Data Depth',
    min: 1,
    max: 5,
    value: 3,
    description: 'How much detail do you want?'
  },
  {
    id: 'challenge',
    label: 'Challenge Level',
    min: 1,
    max: 5,
    value: 3,
    description: 'How challenging should I be?'
  },
  {
    id: 'humor',
    label: 'Humor',
    min: 1,
    max: 5,
    value: 3,
    description: 'How much humor do you enjoy?'
  }
];

// Action types for state machine integration
export type PersonalitySliderActionType = 
  | 'SHOW_PERSONALITY_SLIDERS'
  | 'HIDE_PERSONALITY_SLIDERS'
  | 'UPDATE_SLIDER_VALUE'
  | 'UPDATE_SLIDER_VALUES'
  | 'SET_SLIDER_SUBMITTING'
  | 'SET_SLIDER_ERROR'
  | 'RESET_PERSONALITY_SLIDERS';

export interface PersonalitySliderStateAction {
  type: PersonalitySliderActionType;
  payload?: {
    mode?: 'modal' | 'inline';
    configuration?: SliderConfig[];
    sliderId?: string;
    value?: number;
    values?: Record<string, number>;
    error?: string | null;
    isSubmitting?: boolean;
  };
}
